import { STATIC_CONTENT_ORIGIN, toGameDocumentUrl, toProxyUrl } from './contentUrls';

const DB_NAME = 'ww-offline-store';
const DB_VERSION = 1;

const API_CACHE_NAME = 'ww-api-get-v1';
const APP_CACHE_NAME = 'ww-app-shell-v3';
const STATIC_CACHE_NAME = 'ww-static-content-v1';
const MAP_CACHE_NAME = 'ww-map-content-v1';
const DEFAULT_LANGUAGE = 'en';
const DEFAULT_NETWORK_TIMEOUT_MS = 2500;
const API_SNAPSHOT_PREFIX = 'api:';

const STORE_API_SNAPSHOTS = 'apiSnapshots';
const STORE_PACKAGES = 'packages';
const STORE_COMPLETED_STATIONS = 'completedStations';
const STORE_SYNC_QUEUE = 'syncQueue';
const COMPLETED_TRAIL_STORAGE_PREFIX = 'ww-completed-trail';
const LEGACY_SNAPSHOT_MIGRATION_DECISION_KEY = 'ww-api-snapshot-migration-decision';

let dbPromise = null;
let dbSource = null;
let syncSetupDone = false;
let syncRunPromise = null;
let syncRerunRequested = false;

function getUserId() {
  return localStorage.getItem('uuid') || 'anonymous';
}

function hasRealUserId() {
  return Boolean(localStorage.getItem('uuid'));
}

function setupLegacySnapshotMigrationGate() {
  if (typeof localStorage === 'undefined' || localStorage.getItem(LEGACY_SNAPSHOT_MIGRATION_DECISION_KEY)) {
    return;
  }

  localStorage.setItem(
    LEGACY_SNAPSHOT_MIGRATION_DECISION_KEY,
    hasRealUserId() ? getUserId() : 'none'
  );
}

function canMigrateLegacySnapshots() {
  if (typeof localStorage === 'undefined' || !hasRealUserId()) {
    return false;
  }

  return localStorage.getItem(LEGACY_SNAPSHOT_MIGRATION_DECISION_KEY) === getUserId();
}

function isScopedApiSnapshotKey(key) {
  return typeof key === 'string' && key.startsWith(API_SNAPSHOT_PREFIX);
}

function scopedApiSnapshotKey(key, userId = getUserId()) {
  if (!key || isScopedApiSnapshotKey(key)) {
    return key;
  }

  return `${API_SNAPSHOT_PREFIX}${userId}:${key}`;
}

function getLanguage(lang) {
  return lang || localStorage.getItem('language') || DEFAULT_LANGUAGE;
}

function getStringId(value) {
  return value == null ? '' : String(value);
}

function getStationId(station) {
  if (station == null || typeof station !== 'object') {
    return getStringId(station);
  }

  return getStringId(station.stationid ?? station.id);
}

function getFirstString(...values) {
  const value = values.find((item) => typeof item === 'string' && item.length > 0);
  return value || '';
}

function getContentAssetUrl(regionid, trailid, filename) {
  const contentUrl = process.env.REACT_APP_CONTENT_URL;

  if (!contentUrl) {
    return '';
  }

  return `${contentUrl.replace(/\/+$/, '')}/${regionid}/${trailid}/${filename}`;
}

function completedTrailKey(region, trail, userId = getUserId()) {
  return `${COMPLETED_TRAIL_STORAGE_PREFIX}:${userId}:${region}:${trail}`;
}

function getDateString(value) {
  if (!value) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  return new Date(value).toISOString();
}

function getStoredCompletedTrail(regionid, trailid) {
  if (typeof localStorage === 'undefined') {
    return null;
  }

  try {
    const region = getStringId(regionid);
    const trail = getStringId(trailid);
    const stored = localStorage.getItem(completedTrailKey(region, trail));

    if (!stored) {
      return null;
    }

    return JSON.parse(stored);
  } catch (error) {
    return null;
  }
}

export function markTrailCompletedLocally({
  regionid,
  trailid,
  lang,
  trailTitle,
  badgeUrl,
  stationCount,
  donedate,
  completedAt,
} = {}) {
  if (typeof localStorage === 'undefined' || regionid == null || trailid == null) {
    return null;
  }

  const userId = getUserId();
  const region = getStringId(regionid);
  const trail = getStringId(trailid);
  const existing = getStoredCompletedTrail(region, trail);
  const completedAtValue = completedAt || existing?.completedAt || Date.now();
  const record = {
    key: completedTrailKey(region, trail, userId),
    userId,
    regionid: region,
    trailid: trail,
    language: getFirstString(lang, existing?.language, getLanguage()),
    trail_title: getFirstString(trailTitle, existing?.trail_title, trail),
    badge_url: getFirstString(badgeUrl, existing?.badge_url),
    stations: Number(stationCount || existing?.stations || 0),
    completedAt: completedAtValue,
    donedate: getFirstString(donedate, existing?.donedate, getDateString(completedAtValue)),
    updatedAt: Date.now(),
  };

  try {
    localStorage.setItem(record.key, JSON.stringify(record));
    return record;
  } catch (error) {
    return null;
  }
}

export function getLocalCompletedTrail(regionid, trailid) {
  const record = getStoredCompletedTrail(regionid, trailid);

  if (!record || record.userId !== getUserId()) {
    return null;
  }

  return record;
}

function openDb() {
  if (!('indexedDB' in window)) {
    return Promise.reject(new Error('IndexedDB is not available'));
  }

  if (dbPromise && dbSource === window.indexedDB) {
    return dbPromise;
  }

  dbSource = window.indexedDB;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;

      if (!db.objectStoreNames.contains(STORE_API_SNAPSHOTS)) {
        db.createObjectStore(STORE_API_SNAPSHOTS, { keyPath: 'key' });
      }

      if (!db.objectStoreNames.contains(STORE_PACKAGES)) {
        db.createObjectStore(STORE_PACKAGES, { keyPath: 'key' });
      }

      if (!db.objectStoreNames.contains(STORE_COMPLETED_STATIONS)) {
        db.createObjectStore(STORE_COMPLETED_STATIONS, { keyPath: 'key' });
      }

      if (!db.objectStoreNames.contains(STORE_SYNC_QUEUE)) {
        db.createObjectStore(STORE_SYNC_QUEUE, { keyPath: 'key' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

  return dbPromise;
}

async function withStore(storeName, mode, callback) {
  const db = await openDb();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, mode);
    const store = transaction.objectStore(storeName);
    let callbackResult;

    transaction.oncomplete = () => resolve(callbackResult);
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);

    callbackResult = callback(store);
  });
}

function requestToPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function putRecord(storeName, value) {
  return withStore(storeName, 'readwrite', (store) => store.put(value));
}

async function deleteRecord(storeName, key) {
  return withStore(storeName, 'readwrite', (store) => store.delete(key));
}

async function getRecord(storeName, key) {
  return withStore(storeName, 'readonly', (store) => requestToPromise(store.get(key)));
}

async function getAllRecords(storeName) {
  return withStore(storeName, 'readonly', (store) => requestToPromise(store.getAll()));
}

async function clearStore(storeName) {
  return withStore(storeName, 'readwrite', (store) => store.clear());
}

function isDownloadedContentLocalStorageKey(key) {
  return key.startsWith('offline-') || key.startsWith('offline-map-maxzoom-');
}

function deleteDownloadedContentLocalStorageMarkers() {
  if (typeof localStorage === 'undefined') {
    return;
  }

  const keys = [];

  for (let index = 0; index < localStorage.length; index++) {
    keys.push(localStorage.key(index));
  }

  keys
    .filter((key) => key && isDownloadedContentLocalStorageKey(key))
    .forEach((key) => localStorage.removeItem(key));
}

async function deleteAllCacheStorageEntries() {
  if (typeof caches === 'undefined' || !caches.keys) {
    return [];
  }

  const cacheNames = await caches.keys();
  await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
  return cacheNames;
}

async function clearDownloadedContentIndexedDbRecords() {
  if (typeof window === 'undefined' || !('indexedDB' in window)) {
    return;
  }

  await clearStore(STORE_API_SNAPSHOTS);
  await clearStore(STORE_PACKAGES);
}

export async function deleteAllDownloadedContent() {
  const deletedCaches = await deleteAllCacheStorageEntries();
  await clearDownloadedContentIndexedDbRecords();
  deleteDownloadedContentLocalStorageMarkers();

  return { deletedCaches };
}

export function getPackageKey(client, trail, lang) {
  return `package:${client}:${trail}:${getLanguage(lang)}`;
}

export function getOverviewSnapshotKey(region, lang) {
  return `overview:${region}:${getLanguage(lang)}`;
}

export function getTrailDetailSnapshotKey(trail, lang) {
  return `traildetail:${trail}:${getLanguage(lang)}`;
}

export function getMapSnapshotKey(region, trail, lang) {
  return `map:${region}:${trail}:${getLanguage(lang)}`;
}

export function getContentsSnapshotKey(region, station, lang) {
  return `contents:${region}:${station}:${getLanguage(lang)}`;
}

export function getBadgeSnapshotKey(region, trail, lang) {
  return `badge:${region}:${trail}:${getLanguage(lang)}`;
}

function getBadgeApiUrl(region, trail) {
  return `${process.env.REACT_APP_API_URL}getBadge.php?region=${region}&trailid=${trail}`;
}

function getKnownApiSnapshotKeyFromUrl(url, metadata = {}) {
  let parsedUrl;

  try {
    parsedUrl = new URL(url, window.location.origin);
  } catch (error) {
    return null;
  }

  const endpoint = parsedUrl.pathname.split('/').pop();
  const params = parsedUrl.searchParams;
  const lang = params.get('lang') || metadata.lang || getLanguage();
  const region = params.get('region') || params.get('client') || metadata.client;
  const trail = params.get('trailid') || params.get('trail') || metadata.trail;
  const station = params.get('stationid') || params.get('station');

  if (endpoint === 'overview.php' && region) {
    return getOverviewSnapshotKey(region, lang);
  }

  if (endpoint === 'traildetail.php' && trail) {
    return getTrailDetailSnapshotKey(trail, lang);
  }

  if (endpoint === 'getMap.php' && region && trail) {
    return getMapSnapshotKey(region, trail, lang);
  }

  if (endpoint === 'getContents.php' && region && station) {
    return getContentsSnapshotKey(region, station, lang);
  }

  if (endpoint === 'getBadge.php' && region && trail) {
    return getBadgeSnapshotKey(region, trail, lang);
  }

  return null;
}

function normalizeCacheEntry(entry) {
  if (!entry) {
    return null;
  }

  if (typeof entry === 'string') {
    return { url: entry };
  }

  if (typeof entry.url === 'string') {
    return entry;
  }

  return null;
}

function uniqStrings(values) {
  return Array.from(new Set((values || []).filter(Boolean)));
}

function completedStationKey(region, trail, station, userId = getUserId()) {
  return `completed:${userId}:${region}:${trail}:${station}`;
}

function setStationDoneQueueKey(region, station, userId = getUserId()) {
  return `setStationdone:${userId}:${region}:${station}`;
}

function feedbackQueueKey(region, trail, userId = getUserId()) {
  return `postFeedback:${userId}:${region}:${trail}:${Date.now()}`;
}

function refreshBadgeQueueKey(region, trail, lang, userId = getUserId()) {
  return `refreshBadge:${userId}:${region}:${trail}:${getLanguage(lang)}`;
}

function isBrowserOnline() {
  return typeof navigator === 'undefined' || !('onLine' in navigator) || navigator.onLine;
}

function delay(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function isTimeoutError(error) {
  return error?.name === 'OfflineSnapshotTimeout';
}

async function readApiSnapshotRecord(key) {
  if (!key) {
    return null;
  }

  const scopedKey = scopedApiSnapshotKey(key);
  const scopedSnapshot = await getRecord(STORE_API_SNAPSHOTS, scopedKey).catch(() => null);

  if (scopedSnapshot) {
    return scopedSnapshot;
  }

  if (isScopedApiSnapshotKey(key) || !canMigrateLegacySnapshots()) {
    return null;
  }

  const legacySnapshot = await getRecord(STORE_API_SNAPSHOTS, key).catch(() => null);

  if (!legacySnapshot) {
    return null;
  }

  const migratedSnapshot = {
    ...legacySnapshot,
    key: scopedKey,
    migratedFrom: legacySnapshot.key,
    updatedAt: legacySnapshot.updatedAt || Date.now(),
  };

  await putRecord(STORE_API_SNAPSHOTS, migratedSnapshot).catch(() => null);
  await deleteRecord(STORE_API_SNAPSHOTS, key).catch(() => null);

  return migratedSnapshot;
}

export async function saveApiSnapshot({ key, url, body, packageKey }) {
  if (!key || body === undefined) {
    return null;
  }

  const snapshot = {
    key: scopedApiSnapshotKey(key),
    baseKey: isScopedApiSnapshotKey(key) ? null : key,
    userId: getUserId(),
    url: url || null,
    body,
    packageKey: packageKey || null,
    updatedAt: Date.now(),
  };

  await putRecord(STORE_API_SNAPSHOTS, snapshot);
  return snapshot;
}

export async function saveApiSnapshots(apiSnapshots, packageKey) {
  if (!apiSnapshots) {
    return [];
  }

  const snapshotList = Array.isArray(apiSnapshots)
    ? apiSnapshots
    : Object.entries(apiSnapshots).map(([key, body]) => ({ key, body }));

  const saved = [];

  for (const snapshot of snapshotList) {
    const savedSnapshot = await saveApiSnapshot({
      key: snapshot.key,
      url: snapshot.url,
      body: snapshot.body,
      packageKey,
    });

    if (savedSnapshot) {
      saved.push(savedSnapshot);
    }
  }

  return saved;
}

export async function getApiSnapshot(key) {
  return readApiSnapshotRecord(key);
}

function hasApiControlCode(body) {
  return body && typeof body === 'object' && body.code !== undefined;
}

function isUsableApiSnapshot(snapshot) {
  return snapshot && !hasApiControlCode(snapshot.body);
}

async function deleteApiSnapshot(key) {
  await deleteRecord(STORE_API_SNAPSHOTS, scopedApiSnapshotKey(key)).catch(() => null);

  if (!isScopedApiSnapshotKey(key)) {
    await deleteRecord(STORE_API_SNAPSHOTS, key).catch(() => null);
  }
}

async function fetchJsonAndSaveSnapshot({ key, url, options, packageKey }) {
  const response = await fetch(url, options);

  if (!response.ok) {
    throw new Error(`Network response was not ok for ${url}`);
  }

  const data = await response.json();

  if (!hasApiControlCode(data)) {
    await saveApiSnapshot({ key, url, body: data, packageKey });
  } else {
    await deleteApiSnapshot(key);
  }

  return data;
}

export async function saveOfflinePackage(packageData) {
  const metadata = packageData?.metadata || {};
  const packageKey = metadata.packageKey || getPackageKey(metadata.client, metadata.trail, metadata.lang);
  const savedSnapshots = await saveApiSnapshots(packageData?.apiSnapshots || [], packageKey);
  const cacheLists = packageData?.cacheLists || {};
  const cachedEntries = [
    ...getList(packageData?.cacheEntries).map(normalizeCacheEntry),
    ...getList(packageData?.cachedUrls).map(normalizeCacheEntry),
  ].filter(Boolean);
  const explicitFiles = uniqStrings([
    ...getList(packageData?.files),
    ...getList(packageData?.optionalFiles),
    ...getList(packageData?.mapTiles),
    ...getList(cacheLists.criticalFiles),
    ...getList(cacheLists.optionalFiles),
  ]);
  const apiUrls = uniqStrings([
    ...getList(packageData?.apiUrls),
    ...getList(cacheLists.criticalApiUrls),
    ...getList(cacheLists.optionalApiUrls),
  ]);

  const packageRecord = {
    key: packageKey,
    cacheName: metadata.cacheName || `trail-package-${metadata.trail}-${metadata.lang}`,
    metadata,
    files: packageData?.files || [],
    optionalFiles: packageData?.optionalFiles || [],
    mapTiles: packageData?.mapTiles || [],
    appShellUrls: packageData?.appShellUrls || cacheLists.appShellUrls || [],
    cachedUrls: uniqStrings([
      ...explicitFiles,
      ...cachedEntries.map((entry) => entry.url),
    ]),
    cacheEntries: cachedEntries,
    apiUrls,
    apiSnapshotKeys: uniqStrings([
      ...savedSnapshots.map((snapshot) => snapshot.key),
      ...getList(packageData?.apiSnapshotKeys),
    ]),
    cachedAt: Date.now(),
  };

  await putRecord(STORE_PACKAGES, packageRecord);
  return packageRecord;
}

export async function deleteOfflinePackageSnapshots(client, trail, lang) {
  const packageKey = getPackageKey(client, trail, lang);
  const packageRecord = await getRecord(STORE_PACKAGES, packageKey).catch(() => null);
  const packageSnapshotKeys = new Set(getList(packageRecord?.apiSnapshotKeys));
  const snapshots = await getAllRecords(STORE_API_SNAPSHOTS);

  for (const snapshot of snapshots) {
    if (snapshot.packageKey === packageKey || packageSnapshotKeys.has(snapshot.key)) {
      await deleteRecord(STORE_API_SNAPSHOTS, snapshot.key);
    }
  }

  await deleteRecord(STORE_PACKAGES, packageKey);
}

export async function getOfflineAwareJson({
  key,
  url,
  options = {},
  packageKey,
  timeoutMs = DEFAULT_NETWORK_TIMEOUT_MS,
} = {}) {
  const snapshot = await getApiSnapshot(key).catch(() => null);

  if (snapshot && !isUsableApiSnapshot(snapshot)) {
    await deleteApiSnapshot(key);
  }

  const usableSnapshot = isUsableApiSnapshot(snapshot) ? snapshot : null;
  const request = { key, url, options, packageKey };

  if (!isBrowserOnline()) {
    if (usableSnapshot) {
      return usableSnapshot.body;
    }

    return fetchJsonAndSaveSnapshot(request);
  }

  const networkPromise = fetchJsonAndSaveSnapshot(request);

  if (!usableSnapshot) {
    return networkPromise;
  }

  try {
    return await Promise.race([
      networkPromise,
      delay(timeoutMs).then(() => {
        const error = new Error('Network snapshot refresh timed out');
        error.name = 'OfflineSnapshotTimeout';
        throw error;
      }),
    ]);
  } catch (error) {
    if (isTimeoutError(error)) {
      networkPromise.catch((refreshError) => {
        console.warn('Background API snapshot refresh failed:', refreshError);
      });
      return usableSnapshot.body;
    }

    return usableSnapshot.body;
  }
}

export async function markStationDoneLocally({ regionid, trailid, stationid, timespent }) {
  const userId = getUserId();
  const region = getStringId(regionid);
  const trail = getStringId(trailid);
  const station = getStringId(stationid);
  const completedAt = Date.now();

  const completedRecord = {
    key: completedStationKey(region, trail, station, userId),
    userId,
    regionid: region,
    trailid: trail,
    stationid: station,
    timespent: Number(timespent || 0),
    synced: false,
    completedAt,
  };

  await putRecord(STORE_COMPLETED_STATIONS, completedRecord);

  await putRecord(STORE_SYNC_QUEUE, {
    key: setStationDoneQueueKey(region, station, userId),
    type: 'setStationdone',
    userId,
    payload: {
      regionid: region,
      trailid: trail,
      stationid: station,
      timespent: Number(timespent || 0),
    },
    attempts: 0,
    createdAt: completedAt,
    updatedAt: completedAt,
  });

  return completedRecord;
}

async function markStationSynced(queueItem) {
  const payload = queueItem?.payload || {};
  const key = completedStationKey(payload.regionid, payload.trailid, payload.stationid, queueItem.userId);
  const existing = await getRecord(STORE_COMPLETED_STATIONS, key);

  if (existing) {
    await putRecord(STORE_COMPLETED_STATIONS, {
      ...existing,
      synced: true,
      syncedAt: Date.now(),
    });
  }
}

export async function completeStationOfflineAware({ regionid, trailid, stationid, timespent }) {
  await markStationDoneLocally({ regionid, trailid, stationid, timespent });
  syncPendingOfflineRequests().catch((error) => {
    console.warn('Station completion queued for later sync:', error);
  });

  return { success: true };
}

export async function queueFeedbackOfflineAware({ regionid, trailid, rating, feedback }) {
  const userId = getUserId();

  await putRecord(STORE_SYNC_QUEUE, {
    key: feedbackQueueKey(regionid, trailid, userId),
    type: 'postFeedback',
    userId,
    payload: {
      regionid,
      trailid,
      rating,
      feedback,
    },
    attempts: 0,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  });

  await syncPendingOfflineRequests().catch((error) => {
    console.warn('Feedback queued for later sync:', error);
  });

  return { success: true };
}

export async function getLocalCompletedStations(regionid, trailid) {
  const userId = getUserId();
  const records = await getAllRecords(STORE_COMPLETED_STATIONS);

  return records.filter((record) =>
    record.userId === userId &&
    record.regionid === getStringId(regionid) &&
    record.trailid === getStringId(trailid)
  );
}

export async function clearLocalTrailProgress(regionid, trailid) {
  const userId = getUserId();
  const region = getStringId(regionid);
  const trail = getStringId(trailid);
  const completedStations = await getAllRecords(STORE_COMPLETED_STATIONS);
  const syncQueue = await getAllRecords(STORE_SYNC_QUEUE);

  for (const record of completedStations) {
    if (
      record.userId === userId &&
      record.regionid === region &&
      record.trailid === trail
    ) {
      await deleteRecord(STORE_COMPLETED_STATIONS, record.key);
    }
  }

  for (const queueItem of syncQueue) {
    if (
      queueItem.userId === userId &&
      queueItem.type === 'setStationdone' &&
      getStringId(queueItem.payload?.regionid) === region &&
      getStringId(queueItem.payload?.trailid) === trail
    ) {
      await deleteRecord(STORE_SYNC_QUEUE, queueItem.key);
    }
  }
}

export async function applyLocalProgressToMapData(data, regionid, trailid) {
  if (!data?.stations) {
    return data;
  }

  const completedStations = await getLocalCompletedStations(regionid, trailid);
  const completedByStation = new Map(
    completedStations.map((record) => [getStringId(record.stationid), record])
  );

  return {
    ...data,
    stations: data.stations.map((station) => {
      const stationKey = getStationId(station);
      const completed = completedByStation.get(stationKey);

      if (!completed) {
        return station;
      }

      return {
        ...station,
        done: true,
        donetime: station.donetime || completed.completedAt,
      };
    }),
  };
}

export async function applyLocalProgressToStationData(data, regionid, trailid) {
  if (!data?.stations) {
    return data;
  }

  const completedStations = await getLocalCompletedStations(regionid, trailid || data.trailid);
  const completedIds = new Set(completedStations.map((record) => getStringId(record.stationid)));

  return {
    ...data,
    stations: data.stations.map((station) => ({
      ...station,
      done: station.done || completedIds.has(getStationId(station)),
    })),
  };
}

async function getPackagesForRegion(regionid) {
  const packages = await getAllRecords(STORE_PACKAGES);
  return packages.filter((packageRecord) => getStringId(packageRecord.metadata?.client) === getStringId(regionid));
}

function getPackageStationIds(packageRecord) {
  const metadata = packageRecord?.metadata || {};
  const stationList = [metadata.stationIds, metadata.stationids, metadata.stations]
    .find((candidate) => Array.isArray(candidate)) || [];

  return stationList
    .map(getStationId)
    .filter(Boolean);
}

async function getCachedMapSnapshot(regionid, trailid, lang) {
  const snapshot = await getApiSnapshot(getMapSnapshotKey(regionid, trailid, lang)).catch(() => null);
  return snapshot?.body || null;
}

async function getTrailStationIds(regionid, trailid, lang) {
  const packages = await getPackagesForRegion(regionid);
  const packageRecord = packages.find((record) => getStringId(record.metadata?.trail) === getStringId(trailid));
  const packageStationIds = getPackageStationIds(packageRecord);

  if (packageStationIds.length > 0) {
    return packageStationIds;
  }

  const mapData = await getCachedMapSnapshot(regionid, trailid, lang);

  return (mapData?.stations || [])
    .map(getStationId)
    .filter(Boolean);
}

export async function isTrailCompletedLocally(regionid, trailid, lang) {
  const stationIds = await getTrailStationIds(regionid, trailid, lang);

  if (stationIds.length === 0) {
    return false;
  }

  const completedStations = await getLocalCompletedStations(regionid, trailid);
  const completedIds = new Set(completedStations.map((record) => getStringId(record.stationid)));

  return stationIds.every((stationId) => completedIds.has(getStringId(stationId)));
}

function findTrailInOverview(overviewData, trailid) {
  return (overviewData?.trails || [])
    .find((trail) => getStringId(trail.id) === getStringId(trailid));
}

function markCompletedTrailFromBadgeData(regionid, trailid, lang, data = {}) {
  return markTrailCompletedLocally({
    regionid,
    trailid,
    lang: getFirstString(data.language, lang),
    trailTitle: getFirstString(data.trail_title, data.title),
    badgeUrl: getFirstString(data.badge_url, data.badge),
    stationCount: data.stations || data.stationcount,
    donedate: getFirstString(data.donedate, data.finishdate),
    completedAt: data.completedAt || data.completed_at,
  });
}

function markCompletedTrailFromOverview(regionid, trail, lang) {
  return markTrailCompletedLocally({
    regionid,
    trailid: trail?.id,
    lang,
    trailTitle: trail?.title,
    badgeUrl: getFirstString(trail?.badge_url, trail?.badge),
    stationCount: trail?.stationcount,
    donedate: trail?.donedate,
    completedAt: trail?.completedAt || trail?.completed_at,
  });
}

function getCompletionDoneDate(completion) {
  return getFirstString(
    completion?.donedate,
    getDateString(completion?.completedAt),
    new Date().toISOString()
  );
}

async function buildLocalCompletedBadgeData(regionid, trailid, lang) {
  const language = getLanguage(lang);
  const completedTrail = getLocalCompletedTrail(regionid, trailid);
  const completedByStations = completedTrail
    ? true
    : await isTrailCompletedLocally(regionid, trailid, language).catch(() => false);

  if (!completedTrail && !completedByStations) {
    return null;
  }

  const [overviewSnapshot, mapData, packages] = await Promise.all([
    getApiSnapshot(getOverviewSnapshotKey(regionid, language)).catch(() => null),
    getCachedMapSnapshot(regionid, trailid, language),
    getPackagesForRegion(regionid).catch(() => []),
  ]);

  const overviewData = overviewSnapshot?.body || {};
  const overviewTrail = findTrailInOverview(overviewData, trailid);
  const packageRecord = packages.find((record) => getStringId(record.metadata?.trail) === getStringId(trailid));
  const metadata = packageRecord?.metadata || {};

  return {
    success: true,
    language: getFirstString(completedTrail?.language, overviewData.language, mapData?.language, metadata.lang, language),
    trail_title: getFirstString(
      completedTrail?.trail_title,
      overviewTrail?.title,
      metadata.trailTitle,
      metadata.title,
      mapData?.title,
      getStringId(trailid)
    ),
    badge_url: getFirstString(
      completedTrail?.badge_url,
      overviewTrail?.badge_url,
      overviewTrail?.badge,
      metadata.badgeUrl,
      metadata.badge_url,
      metadata.badge,
      getContentAssetUrl(regionid, trailid, 'badge.png')
    ),
    stations: Number(completedTrail?.stations || overviewTrail?.stationcount || mapData?.stations?.length || 0),
    completedAt: completedTrail?.completedAt,
    donedate: completedTrail?.donedate,
    localOnly: true,
  };
}

async function queueBadgeRefresh({ regionid, trailid, lang, url }) {
  if (regionid == null || trailid == null) {
    return null;
  }

  const userId = getUserId();
  const region = getStringId(regionid);
  const trail = getStringId(trailid);
  const language = getLanguage(lang);
  const queuedAt = Date.now();
  const queueItem = {
    key: refreshBadgeQueueKey(region, trail, language, userId),
    type: 'refreshBadge',
    userId,
    payload: {
      regionid: region,
      trailid: trail,
      lang: language,
      url: url || getBadgeApiUrl(region, trail),
    },
    attempts: 0,
    createdAt: queuedAt,
    updatedAt: queuedAt,
  };

  await putRecord(STORE_SYNC_QUEUE, queueItem);
  return queueItem;
}

async function queueBadgeRefreshAndSync({ regionid, trailid, lang, url }) {
  await queueBadgeRefresh({ regionid, trailid, lang, url });
  syncPendingOfflineRequests().catch((error) => {
    console.warn('Badge refresh queued for later sync:', error);
  });
}

async function fetchBadgeData({ url, options = {} }) {
  const response = await fetch(url, options);

  if (!response.ok) {
    throw new Error('Network response was not ok');
  }

  return response.json();
}

async function readConfirmedSuccess(response, fallbackMessage) {
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }

  let data;

  try {
    data = await response.json();
  } catch (error) {
    throw new Error('Server response was not valid JSON');
  }

  if (hasApiControlCode(data)) {
    throw new Error(`API control code ${data.code}`);
  }

  if (data?.success !== true) {
    throw new Error(data?.error || fallbackMessage);
  }

  return data;
}

async function saveSuccessfulBadgeData({ regionid, trailid, lang, url, data }) {
  await saveApiSnapshot({
    key: getBadgeSnapshotKey(regionid, trailid, lang),
    url,
    body: data,
  });
  markCompletedTrailFromBadgeData(regionid, trailid, lang, data);
  return data;
}

export async function applyLocalProgressToOverview(data, regionid) {
  if (!data?.trails) {
    return data;
  }

  const packages = await getPackagesForRegion(regionid);
  const completedByTrail = new Map();

  for (const trail of data.trails) {
    const trailId = getStringId(trail.id);
    const localCompletion = getLocalCompletedTrail(regionid, trailId);

    if (localCompletion) {
      completedByTrail.set(trailId, localCompletion);
    }

    if (trail.done) {
      completedByTrail.set(
        trailId,
        markCompletedTrailFromOverview(regionid, trail, data.language) || localCompletion || true
      );
    }
  }

  for (const packageRecord of packages) {
    const trail = getStringId(packageRecord.metadata?.trail);

    if (trail && await isTrailCompletedLocally(regionid, trail, data.language)) {
      completedByTrail.set(trail, getLocalCompletedTrail(regionid, trail) || true);
    }
  }

  if (completedByTrail.size === 0) {
    return data;
  }

  return {
    ...data,
    trails: data.trails.map((trail) => {
      const completion = completedByTrail.get(getStringId(trail.id));

      if (!completion) {
        return trail;
      }

      return {
        ...trail,
        done: true,
        donedate: trail.donedate || getCompletionDoneDate(completion),
      };
    }),
  };
}

export async function getBadgeDataOfflineAware({ regionid, trailid, lang, url, options = {} }) {
  const language = getLanguage(lang);
  const badgeUrl = url || getBadgeApiUrl(regionid, trailid);
  const key = getBadgeSnapshotKey(regionid, trailid, language);
  const snapshot = await getApiSnapshot(key).catch(() => null);

  if (snapshot?.body?.success) {
    const completedData = {
      ...snapshot.body,
      success: true,
    };
    markCompletedTrailFromBadgeData(regionid, trailid, language, completedData);
    return completedData;
  }

  const localBadgeData = await buildLocalCompletedBadgeData(regionid, trailid, language);
  if (localBadgeData) {
    markCompletedTrailFromBadgeData(regionid, trailid, language, localBadgeData);
    await queueBadgeRefreshAndSync({ regionid, trailid, lang: language, url: badgeUrl });
    return localBadgeData;
  }

  try {
    const data = await fetchBadgeData({ url: badgeUrl, options });

    if (data?.success) {
      return saveSuccessfulBadgeData({ regionid, trailid, lang: language, url: badgeUrl, data });
    }

    const localBadgeDataAfterServer = await buildLocalCompletedBadgeData(regionid, trailid, language);
    if (localBadgeDataAfterServer) {
      markCompletedTrailFromBadgeData(regionid, trailid, language, localBadgeDataAfterServer);
      await queueBadgeRefreshAndSync({ regionid, trailid, lang: language, url: badgeUrl });
      return localBadgeDataAfterServer;
    }

    return data;
  } catch (error) {
    const snapshot = await getApiSnapshot(key).catch(() => null);

    if (snapshot?.body?.success) {
      const completedData = {
        ...snapshot.body,
        success: true,
      };
      markCompletedTrailFromBadgeData(regionid, trailid, language, completedData);
      return completedData;
    }

    const localBadgeDataAfterError = await buildLocalCompletedBadgeData(regionid, trailid, language);
    if (localBadgeDataAfterError) {
      markCompletedTrailFromBadgeData(regionid, trailid, language, localBadgeDataAfterError);
      await queueBadgeRefreshAndSync({ regionid, trailid, lang: language, url: badgeUrl });
      return localBadgeDataAfterError;
    }

    throw error;
  }
}

async function syncSetStationDone(queueItem) {
  const response = await fetch(`${process.env.REACT_APP_API_URL}setStationdone.php`, {
    method: 'POST',
    body: JSON.stringify({
      regionid: queueItem.payload.regionid,
      stationid: queueItem.payload.stationid,
      timespent: queueItem.payload.timespent,
    }),
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('uuid')}`,
    },
  });

  await readConfirmedSuccess(response, 'Station sync failed');

  await markStationSynced(queueItem);
}

async function syncFeedback(queueItem) {
  const response = await fetch(`${process.env.REACT_APP_API_URL}postFeedback.php`, {
    method: 'POST',
    body: JSON.stringify({
      trailid: queueItem.payload.trailid,
      rating: queueItem.payload.rating,
      feedback: queueItem.payload.feedback,
    }),
    headers: {
      'Content-Type': 'application/json',
    },
  });

  await readConfirmedSuccess(response, 'Feedback sync failed');
}

async function syncRefreshBadge(queueItem) {
  const payload = queueItem?.payload || {};
  const regionid = getStringId(payload.regionid);
  const trailid = getStringId(payload.trailid);
  const lang = getLanguage(payload.lang);
  const url = payload.url || getBadgeApiUrl(regionid, trailid);
  const data = await fetchBadgeData({
    url,
    options: {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('uuid')}`,
      },
    },
  });

  if (data?.success !== true) {
    throw new Error(data?.error || 'Badge refresh failed');
  }

  await saveSuccessfulBadgeData({ regionid, trailid, lang, url, data });
}

function getSyncQueuePriority(queueItem) {
  if (queueItem.type === 'setStationdone') {
    return 0;
  }

  if (queueItem.type === 'refreshBadge') {
    return 1;
  }

  if (queueItem.type === 'postFeedback') {
    return 2;
  }

  return 3;
}

function sortSyncQueue(queue) {
  return [...queue].sort((a, b) => {
    const priorityDiff = getSyncQueuePriority(a) - getSyncQueuePriority(b);

    if (priorityDiff !== 0) {
      return priorityDiff;
    }

    return Number(a.createdAt || 0) - Number(b.createdAt || 0);
  });
}

async function recordSyncFailure(queueItem, error) {
  await putRecord(STORE_SYNC_QUEUE, {
    ...queueItem,
    attempts: Number(queueItem.attempts || 0) + 1,
    lastError: error?.message || String(error),
    updatedAt: Date.now(),
  });
}

async function syncPendingOfflineRequestsOnce() {
  if (typeof navigator !== 'undefined' && 'onLine' in navigator && !navigator.onLine) {
    return { synced: 0, failed: 0 };
  }

  const queue = sortSyncQueue(await getAllRecords(STORE_SYNC_QUEUE));
  let synced = 0;
  let failed = 0;

  for (const queueItem of queue) {
    try {
      if (queueItem.type === 'setStationdone') {
        await syncSetStationDone(queueItem);
      } else if (queueItem.type === 'refreshBadge') {
        await syncRefreshBadge(queueItem);
      } else if (queueItem.type === 'postFeedback') {
        await syncFeedback(queueItem);
      } else {
        continue;
      }

      await deleteRecord(STORE_SYNC_QUEUE, queueItem.key);
      synced++;
    } catch (error) {
      failed++;
      await recordSyncFailure(queueItem, error);
    }
  }

  return { synced, failed };
}

export async function syncPendingOfflineRequests() {
  if (syncRunPromise) {
    syncRerunRequested = true;
    return syncRunPromise;
  }

  syncRunPromise = (async () => {
    const total = { synced: 0, failed: 0 };

    try {
      do {
        syncRerunRequested = false;
        const result = await syncPendingOfflineRequestsOnce();
        total.synced += result.synced;
        total.failed += result.failed;
      } while (syncRerunRequested);

      return total;
    } finally {
      syncRunPromise = null;
    }
  })();

  return syncRunPromise;
}

async function getAssetManifestUrls(origin) {
  try {
    const response = await fetch(`${origin}/asset-manifest.json`, {
      cache: 'no-cache',
    });

    if (!response.ok) {
      return [];
    }

    const manifest = await response.json();
    const urls = new Set([`${origin}/asset-manifest.json`]);

    for (const value of Object.values(manifest.files || {})) {
      if (typeof value === 'string') {
        urls.add(new URL(value, origin).href);
      }
    }

    for (const value of manifest.entrypoints || []) {
      if (typeof value === 'string') {
        urls.add(new URL(value, origin).href);
      }
    }

    return Array.from(urls);
  } catch (error) {
    return [];
  }
}

async function getAppShellUrls() {
  const origin = window.location.origin;
  const urls = new Set([
    `${origin}/`,
    `${origin}/index.html`,
    `${origin}/asset-manifest.json`,
    `${origin}/manifest.json`,
    `${origin}/favicon.ico`,
    `${origin}/logo192.png`,
    `${origin}/logo512.png`,
  ]);

  for (const url of await getAssetManifestUrls(origin)) {
    urls.add(url);
  }

  if ('performance' in window && performance.getEntriesByType) {
    for (const entry of performance.getEntriesByType('resource')) {
      if (
        entry.name?.startsWith(origin) &&
        ['script', 'link', 'css', 'font', 'img'].includes(entry.initiatorType)
      ) {
        urls.add(entry.name);
      }
    }
  }

  return Array.from(urls);
}

function waitForServiceWorkerReady(timeoutMs = 2000) {
  if (!('serviceWorker' in navigator)) {
    return Promise.reject(new Error('Service workers are not available'));
  }

  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise((_, reject) => {
      window.setTimeout(() => reject(new Error('Service worker is not ready')), timeoutMs);
    }),
  ]);
}

function getList(value) {
  return Array.isArray(value) ? value.filter(Boolean) : [];
}

function hasApiSnapshots(apiSnapshots) {
  if (Array.isArray(apiSnapshots)) {
    return apiSnapshots.length > 0;
  }

  return apiSnapshots && Object.keys(apiSnapshots).length > 0;
}

function getStaticContentPathParts(url) {
  if (typeof url !== 'string') {
    return null;
  }

  try {
    const parsedUrl = new URL(url);
    const parts = parsedUrl.pathname.split('/').filter(Boolean);

    if (parsedUrl.origin !== STATIC_CONTENT_ORIGIN || parts[0] !== 'clients') {
      return null;
    }

    return { parsedUrl, parts };
  } catch (error) {
    return null;
  }
}

function toOfflineContentCacheUrl(url) {
  const staticPath = getStaticContentPathParts(url);

  if (!staticPath) {
    return url;
  }

  const { parsedUrl, parts } = staticPath;
  const lastPart = parts[parts.length - 1] || '';
  const isGameDocument =
    (parts.length === 5 && (parsedUrl.pathname.endsWith('/') || !lastPart.includes('.'))) ||
    (parts.length >= 6 && lastPart.toLowerCase() === 'index.html');

  if (isGameDocument) {
    return toGameDocumentUrl(url);
  }

  return parts.length >= 6 ? toProxyUrl(url) : url;
}

function getOfflinePackageCacheLists(packageData, appShellUrls = []) {
  const criticalFiles = getList(packageData?.files).map(toOfflineContentCacheUrl);
  const optionalFiles = [
    ...getList(packageData?.optionalFiles),
    ...getList(packageData?.mapTiles),
    ...getList(appShellUrls),
  ].map(toOfflineContentCacheUrl);
  const apiUrls = getList(packageData?.apiUrls);
  const apiUrlsAreOptional = hasApiSnapshots(packageData?.apiSnapshots);

  return {
    criticalFiles,
    optionalFiles,
    criticalApiUrls: apiUrlsAreOptional ? [] : apiUrls,
    optionalApiUrls: apiUrlsAreOptional ? apiUrls : [],
    appShellUrls,
  };
}

function addPrecacheFailure({ failed, criticalFailed, optionalFailed }, failure, critical) {
  failed.push(failure);

  if (critical) {
    criticalFailed.push(failure);
  } else {
    optionalFailed.push(failure);
  }
}

async function directPrecacheUrls({
  criticalFiles,
  optionalFiles,
  cacheName,
  onProgress,
}) {
  const total = criticalFiles.length + optionalFiles.length;
  let completed = 0;
  const failed = [];
  const criticalFailed = [];
  const optionalFailed = [];

  const fileCache = await caches.open(cacheName);

  const cacheFile = async (url, critical) => {
    try {
      const response = await fetch(url);
      if (response.ok || response.type === 'opaque') {
        await fileCache.put(url, response.clone());
      } else {
        addPrecacheFailure({ failed, criticalFailed, optionalFailed }, { url, status: response.status }, critical);
      }
    } catch (error) {
      addPrecacheFailure({ failed, criticalFailed, optionalFailed }, { url, error: error?.message || String(error) }, critical);
    }

    completed++;
    onProgress?.({ completed, total, url });
  };

  for (const url of criticalFiles) {
    await cacheFile(url, true);
  }

  for (const url of optionalFiles) {
    await cacheFile(url, false);
  }

  return { completed, total, failed, criticalFailed, optionalFailed, cacheEntries: [] };
}

async function cacheApiUrlsAsSnapshots({
  criticalApiUrls,
  optionalApiUrls,
  metadata,
  packageKey,
  authToken,
  onProgress,
}) {
  const total = criticalApiUrls.length + optionalApiUrls.length;
  let completed = 0;
  const failed = [];
  const criticalFailed = [];
  const optionalFailed = [];
  const savedSnapshotKeys = [];

  const cacheApi = async (url, critical) => {
    const snapshotKey = getKnownApiSnapshotKeyFromUrl(url, metadata);

    try {
      if (!snapshotKey) {
        addPrecacheFailure(
          { failed, criticalFailed, optionalFailed },
          { url, error: 'Unknown offline API URL' },
          critical
        );
        return;
      }

      const data = await fetchJsonAndSaveSnapshot({
        key: snapshotKey,
        url,
        packageKey,
        options: {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
          },
        },
      });

      if (hasApiControlCode(data)) {
        addPrecacheFailure(
          { failed, criticalFailed, optionalFailed },
          { url, error: 'API returned a control code' },
          critical
        );
      } else {
        savedSnapshotKeys.push(scopedApiSnapshotKey(snapshotKey));
      }
    } catch (error) {
      addPrecacheFailure(
        { failed, criticalFailed, optionalFailed },
        { url, error: error?.message || String(error) },
        critical
      );
    } finally {
      completed++;
      onProgress?.({ completed, total, url });
    }
  };

  for (const url of criticalApiUrls) {
    await cacheApi(url, true);
  }

  for (const url of optionalApiUrls) {
    await cacheApi(url, false);
  }

  return { completed, total, failed, criticalFailed, optionalFailed, savedSnapshotKeys };
}

function combinePrecacheResults(results) {
  return results.reduce((combined, result) => ({
    completed: combined.completed + Number(result?.completed || 0),
    total: combined.total + Number(result?.total || 0),
    failed: [...combined.failed, ...getList(result?.failed)],
    criticalFailed: [...combined.criticalFailed, ...getList(result?.criticalFailed)],
    optionalFailed: [...combined.optionalFailed, ...getList(result?.optionalFailed)],
    cacheEntries: [...combined.cacheEntries, ...getList(result?.cacheEntries)],
    savedSnapshotKeys: [...combined.savedSnapshotKeys, ...getList(result?.savedSnapshotKeys)],
  }), {
    completed: 0,
    total: 0,
    failed: [],
    criticalFailed: [],
    optionalFailed: [],
    cacheEntries: [],
    savedSnapshotKeys: [],
  });
}

export async function precacheTrailPackage(packageData, onProgress) {
  const metadata = packageData?.metadata || {};
  const cacheName = metadata.cacheName || `trail-package-${metadata.trail}-${metadata.lang}`;
  const cacheLists = getOfflinePackageCacheLists(packageData, await getAppShellUrls());
  const authToken = localStorage.getItem('uuid') || '';
  const packageKey = metadata.packageKey || getPackageKey(metadata.client, metadata.trail, metadata.lang);
  let fileResult;

  try {
    const registration = await waitForServiceWorkerReady();
    const worker = navigator.serviceWorker.controller || registration.active || registration.waiting || registration.installing;

    if (!worker) {
      throw new Error('No active service worker');
    }

    fileResult = await new Promise((resolve, reject) => {
      const channel = new MessageChannel();

      channel.port1.onmessage = (event) => {
        const message = event.data || {};

        if (message.type === 'PRECACHE_PROGRESS') {
          onProgress?.(message.payload);
          return;
        }

        if (message.type === 'PRECACHE_COMPLETE') {
          resolve(message.payload);
          return;
        }

        if (message.type === 'PRECACHE_ERROR') {
          reject(new Error(message.error || 'Service worker precache failed'));
        }
      };

      worker.postMessage({
        type: 'PRECACHE_TRAIL_PACKAGE',
        payload: {
          ...cacheLists,
          files: cacheLists.criticalFiles,
          apiUrls: [],
          criticalApiUrls: [],
          optionalApiUrls: [],
          cacheName,
          authToken,
        },
      }, [channel.port2]);
    });
  } catch (error) {
    console.warn('Falling back to page precache:', error);
    fileResult = await directPrecacheUrls({ ...cacheLists, cacheName, onProgress });
  }

  const apiResult = await cacheApiUrlsAsSnapshots({
    criticalApiUrls: cacheLists.criticalApiUrls,
    optionalApiUrls: cacheLists.optionalApiUrls,
    metadata,
    packageKey,
    authToken,
    onProgress,
  });

  const combined = combinePrecacheResults([fileResult, apiResult]);
  packageData.cacheLists = cacheLists;
  packageData.cacheEntries = combined.cacheEntries;
  packageData.cachedUrls = combined.cacheEntries.map((entry) => entry.url);
  packageData.apiSnapshotKeys = uniqStrings([
    ...getList(packageData.apiSnapshotKeys),
    ...getList(apiResult.savedSnapshotKeys),
  ]);

  return combined;
}

export async function deleteTrailPackageCache({ client, trail, lang, cacheName }) {
  const resolvedCacheName = cacheName || `trail-package-${trail}-${getLanguage(lang)}`;
  const packageKey = getPackageKey(client, trail, getLanguage(lang));
  const packageRecord = await getRecord(STORE_PACKAGES, packageKey).catch(() => null);

  if ('caches' in window) {
    await caches.delete(resolvedCacheName);
    await caches.delete(`trail-assets-${trail}-${getLanguage(lang)}`);

    const cacheEntries = getList(packageRecord?.cacheEntries)
      .map(normalizeCacheEntry)
      .filter(Boolean);
    const entriesByCache = new Map();

    for (const entry of cacheEntries) {
      if (!entry.cacheName) {
        continue;
      }

      const urls = entriesByCache.get(entry.cacheName) || [];
      urls.push(entry.url);
      entriesByCache.set(entry.cacheName, urls);
    }

    for (const [entryCacheName, urls] of entriesByCache.entries()) {
      const cache = await caches.open(entryCacheName);

      for (const url of urls) {
        await cache.delete(url);
      }
    }

    const cachedUrls = uniqStrings([
      ...getList(packageRecord?.cachedUrls),
      ...getList(packageRecord?.files),
      ...getList(packageRecord?.optionalFiles),
      ...getList(packageRecord?.mapTiles),
      ...getList(packageRecord?.appShellUrls),
      ...getList(packageRecord?.apiUrls),
    ]);

    if (cachedUrls.length > 0) {
      const fallbackCacheNames = [
        resolvedCacheName,
        STATIC_CACHE_NAME,
        MAP_CACHE_NAME,
        API_CACHE_NAME,
        'ww-app-shell-v2',
        APP_CACHE_NAME,
      ];

      for (const fallbackCacheName of fallbackCacheNames) {
        const cache = await caches.open(fallbackCacheName);

        for (const url of cachedUrls) {
          await cache.delete(url);
        }
      }
    }
  }

  await deleteOfflinePackageSnapshots(client, trail, getLanguage(lang));

  if ('serviceWorker' in navigator) {
    const registration = await navigator.serviceWorker.ready.catch(() => null);
    const worker = navigator.serviceWorker.controller || registration?.active;

    worker?.postMessage({
      type: 'DELETE_TRAIL_PACKAGE',
      payload: {
        cacheName: resolvedCacheName,
      },
    });
  }
}

export function setupOfflineSync() {
  if (syncSetupDone || typeof window === 'undefined') {
    return;
  }

  syncSetupDone = true;
  setupLegacySnapshotMigrationGate();
  window.addEventListener('online', () => {
    syncPendingOfflineRequests().catch((error) => {
      console.warn('Offline sync failed:', error);
    });
  });

  syncPendingOfflineRequests().catch(() => {});
}
